import { createContext, useContext, useEffect, useState } from "react";
import { api } from "../api/client";
import { FREE_REFERENCE_FRAMEWORKS, UNPAID_FRAMEWORK_IDS, ALTERNATIVE_FRAMEWORK_GROUPS, type FrameworkId } from "../lib/frameworkCatalogue";

// Which kind of thing each framework is (certifiable, AI-assessable, scoreable, its fee, the
// requirement rows it shares) lives in lib/frameworkCatalogue.ts, the mirror of the backend's
// lib/frameworks.ts catalogue; the sets below are derived from it and re-exported here for the pages.
export {
  FRAMEWORK_ORDER, FRAMEWORK_LABELS, AI_ASSESSABLE_FRAMEWORKS, CERTIFIABLE_FRAMEWORKS, FREE_REFERENCE_FRAMEWORKS,
  UNPAID_FRAMEWORK_IDS, ESG_FRAMEWORK_IDS, ALTERNATIVE_FRAMEWORK_GROUPS, CE_FRAMEWORK_IDS, UNSCOREABLE_FRAMEWORK_IDS,
  requirementFrameworkId, withRequirementFrameworks,
} from "../lib/frameworkCatalogue";
export type { FrameworkId, CertifiableFrameworkId } from "../lib/frameworkCatalogue";

export function isPaidStandard(frameworkId: string): boolean {
  return !UNPAID_FRAMEWORK_IDS.has(frameworkId);
}

export function hasPaidStandard(frameworkIds: Iterable<string>): boolean {
  for (const id of frameworkIds) if (isPaidStandard(id)) return true;
  return false;
}

/** A selection with `id` added and any other level of the same scheme removed. */
export function withAlternative<T extends string>(frameworkIds: T[], id: T): T[] {
  const group = ALTERNATIVE_FRAMEWORK_GROUPS.find((g) => g.includes(id));
  const rest = group ? frameworkIds.filter((x) => x === id || !group.includes(x)) : frameworkIds;
  return rest.includes(id) ? rest : [...rest, id];
}

/** A selection with the free reference frameworks dropped when no paid standard is left to carry them. */
export function withoutOrphanedReferenceFrameworks<T extends string>(frameworkIds: T[]): T[] {
  return hasPaidStandard(frameworkIds) ? frameworkIds : frameworkIds.filter((id) => !FREE_REFERENCE_FRAMEWORKS.has(id as FrameworkId));
}

export interface OrgFramework {
  id: string;
  frameworkId: FrameworkId;
  enrolledAt: string;
  pciSaqTypes?: string[];
}

interface FrameworksContextType {
  frameworks: OrgFramework[];
  enrolled: Set<FrameworkId>;
  pciSaqTypes: string[];
  loading: boolean;
  reload: () => void;
}

const FrameworksContext = createContext<FrameworksContextType>({
  frameworks: [],
  enrolled: new Set(),
  pciSaqTypes: [],
  loading: true,
  reload: () => {},
});

export function FrameworksProvider({ children }: { children: React.ReactNode }) {
  const [frameworks, setFrameworks] = useState<OrgFramework[]>([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setLoading(true);
    api
      .get<{ frameworks: OrgFramework[] }>("/organizations/frameworks")
      .then((d) => setFrameworks(d.frameworks))
      .catch(() => setFrameworks([]))
      .finally(() => setLoading(false));
  }, [tick]);

  const enrolled = new Set(frameworks.map((f) => f.frameworkId));
  const pciSaqTypes = frameworks.find((f) => f.frameworkId === "pci_dss")?.pciSaqTypes ?? [];

  return (
    <FrameworksContext.Provider value={{ frameworks, enrolled, pciSaqTypes, loading, reload: () => setTick((t) => t + 1) }}>
      {children}
    </FrameworksContext.Provider>
  );
}

export function useFrameworks() {
  return useContext(FrameworksContext);
}
